import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  TextInput,
  Alert,
  Platform,
  Modal,
  ScrollView,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

// Component imports
import { AttendeeListItem } from './AttendeeListItem';
import { FilterModal } from './FilterModal';
import { ResidentDetailsModal } from './ResidentDetailsModal';

// Types
import { EventAttendanceTabProps } from './types';
import { EVENT_FILTER_LABELS } from '@/types/admin/events';
import { StatsCard } from '@/components/common/admin/mobile/StatsCard';
import { useEventAttendance } from './hooks/useEventAttendance';
import { DesktopResidentDetailsPanel } from './components/DesktopResidentDetailsPanel';
import { PaginationControls } from './components/PaginationControls';
import { FilterDropdown } from './components/FilterDropdown';

export const EventAttendanceTab: React.FC<EventAttendanceTabProps> = ({ event }) => {
  const state = useEventAttendance(event);

  // state & handlers
  const {
    attendeeList,
    totalAttendees,
    totalPages,
    stats,
    selectedResident,
    selectedResidentFlags,
    currentPage,
    searchQuery,
    showFilters,
    showDropdown,
    triggerRef,
    anchor,
    isFetching,
    isLoading,
    error,
    pagingForward,
    isFilterRefetch,
    lastRefetchSource,
    isUpdatingRsvp,
    attendeeFilters,
    hasActiveFilters,
    setShowFilters,
    setShowDropdown,
    setAnchor,
    setSelectedResident,
    handlePreviousPage,
    handleNextPage,
    toggleFilter,
    onSearchChange,
    handleCheckIn,
    handleExport,
  } = state as any;

  // Initial loading state (no data yet)
  const isInitialLoad = isLoading && !state.attendanceData;
  if (isInitialLoad) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={ADMIN_COLORS.PRIMARY} />
        <Text style={styles.loadingText}>Loading attendance...</Text>
      </View>
    );
  }

  // Error state
  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Failed to load attendance</Text>
        <Text style={styles.errorSubtext}>Please try again later</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Stats Section */}
      <View style={styles.statsSection}>
        <View style={styles.statsRow}>
          <StatsCard
            title="Checked In"
            value={stats.checkedIn}
            color={ADMIN_COLORS.SUCCESS}
            icon="✓"
          />
          <StatsCard
            title="Total RSVPs"
            value={stats.rsvps}
            color={ADMIN_COLORS.PRIMARY}
            icon="👥"
          />
        </View>
      </View>

      {/* Search and Filter Section */}
      <View style={styles.controlsSection}>
        <View style={styles.searchContainer}>
          <View style={styles.searchWrapper}>
            <TextInput
              style={[styles.searchInput, (isFetching && isFilterRefetch && lastRefetchSource === 'search') && { paddingRight: 40 }]}
              placeholder="Search residents..."
              placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
              value={searchQuery}
              onChangeText={onSearchChange}
              accessibilityLabel="Search residents"
              accessibilityHint="Type to search for residents by name"
              accessibilityRole="search"
            />
            {(isFetching && isFilterRefetch && lastRefetchSource === 'search') && (
              <View style={styles.searchSpinnerContainer} pointerEvents="none">
                <ActivityIndicator size="small" color={ADMIN_COLORS.TEXT_SECONDARY} />
              </View>
            )}
          </View>
        </View>
        {Platform.OS === 'web' ? (
          <View style={styles.filterDropdownContainer} ref={triggerRef}>
            <TouchableOpacity
              style={[styles.filterButtonIcon, showDropdown && styles.filterButtonIconActive]}
              onPress={() => {
                const node: any = triggerRef.current;
                if (node && node.measureInWindow) {
                  node.measureInWindow((x: number, y: number, w: number, h: number) => {
                    setAnchor({ x, y, w, h });
                    setShowDropdown(true);
                  });
                } else {
                  setShowDropdown(true);
                }
              }}
              accessibilityLabel={hasActiveFilters ? 'Filter attendees (filters active)' : 'Filter attendees'}
              accessibilityHint="Tap to toggle filter options"
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.FILTER width={16} height={16} color={ADMIN_COLORS.TEXT_PRIMARY} />
              {hasActiveFilters && <View style={styles.filterDot} />}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.filterButton, hasActiveFilters && styles.filterButtonActive]}
            onPress={() => setShowFilters(true)}
            accessibilityLabel={hasActiveFilters ? 'Filter attendees (filters active)' : 'Filter attendees'}
            accessibilityHint="Tap to open filter options for attendee list"
            accessibilityRole="button"
          >
            <Text style={styles.filterButtonText}>Filter</Text>
            {hasActiveFilters && <View style={styles.filterDot} />}
          </TouchableOpacity>
        )}
      </View>

      {/* Web dropdown rendered via anchored portal (handles backdrop close internally) */}
      {Platform.OS === 'web' && showDropdown && (
        <FilterDropdown 
          anchor={anchor} 
          filters={attendeeFilters} 
          onToggle={toggleFilter}
          onRequestClose={() => setShowDropdown(false)}
        />
      )}

      {Platform.OS === 'web' ? (
        <View style={styles.desktopRow}>
          <View style={styles.desktopListColumn}>
            <View style={styles.listSection}>
              <View style={styles.listHeader}>
                <Text style={styles.listTitle}>
                  Check-in Status ({totalAttendees})
                </Text>
                <TouchableOpacity 
                  style={styles.exportButton}
                  accessibilityLabel="Export attendee list"
                  accessibilityHint="Tap to export the current attendee list to CSV"
                  accessibilityRole="button"
                  onPress={handleExport}
                >
                  <Text style={styles.exportButtonText}>Export</Text>
                </TouchableOpacity>
              </View>
              {isFetching && !pagingForward && isFilterRefetch && lastRefetchSource === 'filter' && (
                <View style={styles.listLoadingOverlay} pointerEvents="none">
                  <View style={styles.listLoadingInner}>
                    <ActivityIndicator size="small" color={ADMIN_COLORS.PRIMARY} />
                    <Text style={styles.listLoadingText}>Updating list...</Text>
                  </View>
                </View>
              )}
              {!isFetching && attendeeList.length === 0 ? (
                <View style={styles.noAttendeesContainer}>
                  <Text style={styles.noAttendeesText}>No attendees found</Text>
                  <Text style={styles.noAttendeesSubtext}>
                    Try adjusting your filters or search query
                  </Text>
                </View>
              ) : (
                <>
                  <FlatList
                    data={attendeeList}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={({ item }) => (
                      <AttendeeListItem
                        resident={item}
                        eventId={event.id}
                        onPress={() => setSelectedResident(item)}
                      />
                    )}
                    style={styles.attendeeList}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={styles.listContent}
                  />
                  {totalPages > 1 && (
                    <PaginationControls
                      page={currentPage}
                      totalPages={totalPages}
                      isFetching={isFetching}
                      pagingForward={pagingForward}
                      onPrev={handlePreviousPage}
                      onNext={handleNextPage}
                    />
                  )}
                </>
              )}
            </View>
          </View>
          <View style={styles.desktopDetailsColumn}>
            {!selectedResident ? (
              <View style={styles.desktopEmptyState}> 
                <Text style={styles.desktopEmptyTitle}>Select resident</Text>
                <Text style={styles.desktopEmptySubtitle}>Review the resident's information and check them in</Text>
              </View>
            ) : (
              <DesktopResidentDetailsPanel
                resident={selectedResident}
                flags={selectedResidentFlags}
                isUpdating={isUpdatingRsvp}
                onCheckIn={handleCheckIn}
              />
            )}
          </View>
        </View>
      ) : (
        <>
          {/* Attendee List (mobile) */}
          <View style={styles.listSection}>
            <View style={styles.listHeader}>
              <Text style={styles.listTitle}>
                Check-in Status ({totalAttendees})
              </Text>
              <TouchableOpacity 
                style={styles.exportButton}
                accessibilityLabel="Export attendee list"
                accessibilityHint="Tap to export the current attendee list to CSV"
                accessibilityRole="button"
                onPress={handleExport}
              >
                <Text style={styles.exportButtonText}>Export</Text>
              </TouchableOpacity>
            </View>
            {isFetching && !pagingForward && isFilterRefetch && lastRefetchSource === 'filter' && (
              <View style={styles.listLoadingOverlay} pointerEvents="none">
                <View style={styles.listLoadingInner}>
                  <ActivityIndicator size="small" color={ADMIN_COLORS.PRIMARY} />
                  <Text style={styles.listLoadingText}>Updating list...</Text>
                </View>
              </View>
            )}
            {!isFetching && attendeeList.length === 0 ? (
              <View style={styles.noAttendeesContainer}>
                <Text style={styles.noAttendeesText}>No attendees found</Text>
                <Text style={styles.noAttendeesSubtext}>
                  Try adjusting your filters or search query
                </Text>
              </View>
            ) : (
              <>
                <FlatList
                  data={attendeeList}
                  keyExtractor={(item) => item.id.toString()}
                  renderItem={({ item }) => (
                    <AttendeeListItem
                      resident={item}
                      eventId={event.id}
                      onPress={() => setSelectedResident(item)}
                    />
                  )}
                  style={styles.attendeeList}
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.listContent}
                />
                {totalPages > 1 && (
                  <View style={styles.paginationContainer}>
                    <View style={styles.paginationInfo}>
                      <Text style={styles.paginationText}>
                        Page {currentPage + 1} of {totalPages}
                      </Text>
                    </View>
                    <View style={styles.paginationControls}>
                      <TouchableOpacity
                        style={[
                          styles.paginationButton,
                          currentPage === 0 && styles.paginationButtonDisabled,
                        ]}
                        onPress={handlePreviousPage}
                        disabled={currentPage === 0 || isFetching}
                        accessibilityLabel="Previous page"
                        accessibilityHint={currentPage === 0 ? "No previous page available" : "Go to previous page of attendees"}
                        accessibilityRole="button"
                      >
                        <Text style={styles.paginationButtonText}>Previous</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.paginationButton,
                          currentPage >= totalPages - 1 && styles.paginationButtonDisabled,
                          isFetching && pagingForward && styles.paginationButtonLoading,
                        ]}
                        onPress={handleNextPage}
                        disabled={currentPage >= totalPages - 1 || isFetching}
                        accessibilityLabel="Next page"
                        accessibilityHint={currentPage >= totalPages - 1 ? "No next page available" : "Go to next page of attendees"}
                        accessibilityRole="button"
                      >
                        {isFetching && pagingForward ? (
                          <View style={styles.buttonSpinnerRow}>
                            <ActivityIndicator size="small" color={'#FFFFFF'} />
                          </View>
                        ) : (
                          <Text style={styles.paginationButtonText}>Next</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </>
            )}
          </View>

          {/* Modals (mobile) */}
          <FilterModal
            visible={showFilters}
            filters={attendeeFilters}
            onToggleFilter={toggleFilter}
            onClose={() => setShowFilters(false)}
          />

          <ResidentDetailsModal
            visible={!!selectedResident}
            resident={selectedResident}
            onClose={() => setSelectedResident(null)}
            onCheckIn={handleCheckIn}
            eventId={event.id}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    position: 'relative',
  },
  // Stats Section
  statsSection: {
    marginBottom: 24,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  // Controls Section
  controlsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  searchContainer: {
    flex: 1,
  },
  searchInput: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER + '30',    
  },
  searchWrapper: {
    position: 'relative',
    justifyContent: 'center',
  },
  searchSpinnerContainer: {
    position: 'absolute',
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER + '30',
    position: 'relative',
  },
  filterButtonActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  filterButtonIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER + '30',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 21,
  },
  filterButtonIconActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  filterDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  filterDropdownContainer: {
    position: 'relative',
    zIndex: 1001,
  },
  dropdownPane: {
    position: 'absolute',
    top: 48,
    right: 0,
    width: 240,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 8,
    zIndex: 1000,
  },
  dropdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  checkboxBox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    marginRight: 10,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
  checkboxChecked: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  checkboxUnchecked: {},
  dropdownText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
  },
  // List Section
  listSection: {
    flex: 1,
    position: 'relative',
  },
  listLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 48,
    backgroundColor: 'transparent',
    zIndex: 1000,
  },
  listLoadingInner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  listLoadingText: {
    marginTop: 6,
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  listTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  exportButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  exportButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  attendeeList: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 20,
  },
  // Empty State
  noAttendeesContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  noAttendeesText: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  noAttendeesSubtext: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 24,
  },
  // Pagination
  paginationContainer: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER + '40',
  },
  paginationInfo: {
    alignItems: 'center',
    marginBottom: 12,
  },
  paginationText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  paginationControls: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  paginationButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
    minWidth: 100,
    alignItems: 'center',
  },
  paginationButtonLoading: {
    opacity: 0.9,
  },
  buttonSpinnerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paginationButtonDisabled: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
  paginationButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  // Loading and Error States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  errorText: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  // Desktop split layout
  desktopRow: {
    flexDirection: 'row',
    gap: 16,
    minHeight: 0,
    flex: 1,
  },
  desktopListColumn: {
    flex: 1,
    minWidth: 360,
    maxWidth: 560,
    borderRightWidth: 1,
    borderRightColor: ADMIN_COLORS.BORDER,
    paddingRight: 16,
    overflow: 'hidden',
  },
  desktopDetailsColumn: {
    flex: 1,
    paddingLeft: 16,
  },
  desktopEmptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 24,
    paddingBottom: 24,
  },
  desktopEmptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  desktopEmptySubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  detailsPanelContainer: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    overflow: 'hidden',
  },
  detailsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  detailsHeaderTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  detailsContent: {
    padding: 16,
  },
  residentInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  residentAvatarLg: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  residentAvatarLgText: {
    fontSize: 20,
    fontWeight: '700',
    color: ADMIN_COLORS.PRIMARY,
  },
  residentNameLg: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  residentEmailText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 2,
  },
  residentUnitText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 2,
  },
  residentStatusPill: {
    alignSelf: 'flex-start',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginTop: 6,
  },
  residentStatusPillText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  checkinSectionDesktop: {
    marginTop: 8,
  },
  checkinSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  checkedInStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.SUCCESS + '15',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.SUCCESS + '30',
    alignSelf: 'flex-start',
  },
  checkedInDotSmall: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.SUCCESS,
    marginRight: 8,
  },
  checkedInStatusText: {
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.SUCCESS,
  },
  notCheckedInStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY + '15',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.TEXT_TERTIARY + '30',
    alignSelf: 'flex-start',
  },
  notCheckedInDotSmall: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.TEXT_TERTIARY,
    marginRight: 8,
  },
  notCheckedInStatusText: {
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  rsvpInfoBox: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '15',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  rsvpInfoText: {
    fontSize: 12,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
  },
  checkInButtonDesktop: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  checkInButtonDesktopText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  checkOutButtonDesktop: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  checkOutButtonDesktopText: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    fontWeight: '600',
  },
});